import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { launchesApi, launchTypesApi } from "../../api/endpoints";
import type { Launch, LaunchType, TypeSetKey } from "../../types/domain";
import { IcoCheck, IcoClose, IcoCopy, IcoSparkle } from "../../components/icons";
import { DynamicIcon } from "../../components/DynamicIcon";
import { ModalBackdrop } from "../../components/ModalBackdrop";
import { buildDays, pluralDays, TYPE_SETS } from "../../lib/launchPresets";

const todayPlusDays = (offset: number) => {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
};

interface Props {
  existingLaunches: Launch[];
  onClose: () => void;
  onCreated: (launch: Launch) => void;
}

export function NewLaunchModal({ existingLaunches, onClose, onCreated }: Props) {
  const qc = useQueryClient();

  const launchTypesQuery = useQuery({
    queryKey: ["launchTypes"],
    queryFn: async () => (await launchTypesApi.list()).launchTypes,
  });
  const launchTypes = launchTypesQuery.data ?? [];

  const [launchTypeId, setLaunchTypeId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [goal, setGoal] = useState("");
  const [startDate, setStartDate] = useState(() => todayPlusDays(14));
  const [warmup, setWarmup] = useState(4);
  const [broadcast, setBroadcast] = useState(3);
  const [followup, setFollowup] = useState(3);
  const [typeSet, setTypeSet] = useState<TypeSetKey>("full");
  const [seedCards, setSeedCards] = useState(true);
  const [cloneFrom, setCloneFrom] = useState<string>("");

  // Как только типы запусков загрузились — выбираем default: вебинар (его
  // запускают чаще всего), если он есть, иначе первый из списка.
  // Префилим слайдеры из daysByStage (реальная структура шаблона). Если
  // шаблон пустой — универсальный стартовый 3/1/2.
  useEffect(() => {
    if (launchTypes.length === 0) return;
    if (launchTypeId !== null) return;
    const first = launchTypes.find((t) => t.id === "webinar") ?? launchTypes[0]!;
    setLaunchTypeId(first.id);
    const ds = first.daysByStage;
    const total = ds ? ds.warmup + ds.sales + ds.close : 0;
    setWarmup(total > 0 ? ds!.warmup : 3);
    setBroadcast(total > 0 ? ds!.sales : 1);
    setFollowup(total > 0 ? ds!.close : 2);
    setTypeSet(first.typeSet);
  }, [launchTypes, launchTypeId]);

  const selectedType: LaunchType | null = useMemo(
    () => launchTypes.find((t) => t.id === launchTypeId) ?? null,
    [launchTypes, launchTypeId],
  );
  const totalDays = warmup + broadcast + followup;
  const cloneSource = cloneFrom ? existingLaunches.find((l) => l.id === cloneFrom) : null;

  const pickType = (id: string) => {
    const t = launchTypes.find((x) => x.id === id);
    if (!t) return;
    setLaunchTypeId(id);
    const ds = t.daysByStage;
    const total = ds ? ds.warmup + ds.sales + ds.close : 0;
    setWarmup(total > 0 ? ds!.warmup : 3);
    setBroadcast(total > 0 ? ds!.sales : 1);
    setFollowup(total > 0 ? ds!.close : 2);
    setTypeSet(t.typeSet);
    setCloneFrom("");
  };

  const createMutation = useMutation({
    mutationFn: () =>
      launchesApi.create({
        name: name.trim(),
        emoji: undefined,
        color: selectedType?.color,
        d1StartDate: startDate,
        goal: goal.trim() || undefined,
        days: buildDays(warmup, broadcast, followup),
        launchTypeId: selectedType?.id ?? null,
        // Новый запуск сразу активен: в 99% случаев его создают, чтобы тут же
        // работать, а не откладывать в черновики.
        status: "active",
        // Seed только если чекбокс включён и в TypeSet есть хоть один тип —
        // "empty" без фильтра не попадёт в сид, сервер всё равно пропустит.
        ...(seedCards && typeSet !== "empty" ? { seedCards: { typeSetKey: typeSet } } : {}),
      }),
    onSuccess: ({ launch }) => {
      qc.invalidateQueries({ queryKey: ["launches"] });
      onCreated(launch);
    },
  });

  const cloneMutation = useMutation({
    mutationFn: (sourceId: string) =>
      launchesApi.clone(sourceId, {
        name: name.trim(),
        d1StartDate: startDate,
        copyCards: true,
      }),
    onSuccess: ({ launch }) => {
      qc.invalidateQueries({ queryKey: ["launches"] });
      onCreated(launch);
    },
  });

  const isPending = createMutation.isPending || cloneMutation.isPending;
  const error = createMutation.error || cloneMutation.error;

  const canSubmit =
    name.trim().length > 0 &&
    !isPending &&
    (cloneFrom !== ""
      ? Boolean(cloneSource)
      : totalDays > 0 && totalDays <= 30 && Boolean(selectedType));

  const submit = () => {
    if (!canSubmit) return;
    if (cloneFrom) {
      cloneMutation.mutate(cloneFrom);
    } else {
      createMutation.mutate();
    }
  };

  const segments = useMemo(() => {
    const out: Array<{ label: string; days: number; color: string }> = [];
    if (warmup > 0) out.push({ label: "Прогрев", days: warmup, color: "#3B82F6" });
    if (broadcast > 0) out.push({ label: "Эфиры", days: broadcast, color: "var(--accent)" });
    if (followup > 0) out.push({ label: "Дожим", days: followup, color: "#F59E0B" });
    return out;
  }, [warmup, broadcast, followup]);

  return (
    <ModalBackdrop onClose={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div
            className="launch-dot"
            style={{ background: "linear-gradient(135deg, var(--accent), var(--accent-2))" }}
          >
            <IcoSparkle size={12} stroke="white" sw={2} />
          </div>
          <h2>Новый запуск</h2>
          <div className="spacer" />
          <button className="modal-close" onClick={onClose} aria-label="Закрыть">
            <IcoClose size={16} />
          </button>
        </div>

        <div className="modal-body">
          {existingLaunches.length > 0 && (
            <div className="form-row">
              <label className="form-label">Основа</label>
              <div className="clone-row">
                <button
                  type="button"
                  className={"clone-opt" + (cloneFrom === "" ? " active" : "")}
                  onClick={() => setCloneFrom("")}
                >
                  <IcoSparkle size={14} />
                  <div>
                    <b>Новый</b>
                    <span>С нуля или по пресету</span>
                  </div>
                </button>
                {existingLaunches.map((l) => (
                  <button
                    key={l.id}
                    type="button"
                    className={"clone-opt" + (cloneFrom === l.id ? " active" : "")}
                    onClick={() => setCloneFrom(l.id)}
                  >
                    <IcoCopy size={14} />
                    <div>
                      <b>Копия: {l.name}</b>
                      <span>{l._count?.cards ?? 0} карточек</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {cloneFrom === "" && (
            <div className="form-row">
              <label className="form-label">Тип запуска</label>
              {launchTypesQuery.isLoading ? (
                <div className="preset-hint">Загружаем типы…</div>
              ) : launchTypes.length === 0 ? (
                <div className="preset-hint" style={{ color: "#DC2626" }}>
                  Нет доступных типов запусков. Перезапустите сервер или создайте тип в разделе «Типы запусков».
                </div>
              ) : (
                <>
                  <div className="preset-grid">
                    {launchTypes.map((t) => {
                      const isActive = launchTypeId === t.id;
                      return (
                        <button
                          key={t.id}
                          type="button"
                          className={"preset-card" + (isActive ? " active" : "")}
                          onClick={() => pickType(t.id)}
                        >
                          <div
                            className="preset-icon"
                            style={isActive ? { background: `${t.color}20` } : undefined}
                          >
                            <DynamicIcon
                              name={t.icon}
                              size={22}
                              color={isActive ? t.color : "var(--text-2)"}
                              strokeWidth={1.5}
                            />
                          </div>
                          <b>{t.name}</b>
                          <span>{t.description ?? ""}</span>
                        </button>
                      );
                    })}
                  </div>
                  {selectedType?.hint && (
                    <div className="preset-hint">
                      <IcoSparkle size={12} stroke="var(--text-3)" />
                      {selectedType.hint}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <div className="form-row">
            <label className="form-label">Название запуска</label>
            <input
              className="form-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={
                cloneSource
                  ? `Например: Копия — ${cloneSource.name}`
                  : selectedType?.id === "marathon"
                    ? "Например: Марафон «Запуск без слёз»"
                    : selectedType?.id === "webinar"
                      ? "Например: Вебинар по холодному трафику"
                      : "Название запуска"
              }
              autoFocus
            />
          </div>

          {cloneFrom === "" && (
            <div className="form-row">
              <label className="form-label">
                Цель{" "}
                <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, opacity: 0.7 }}>
                  (необязательно)
                </span>
              </label>
              <input
                className="form-input"
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                placeholder="Например: 150 регистраций и 2 млн выручки"
              />
            </div>
          )}

          {cloneFrom === "" ? (
            <>
              <div className="form-row">
                <label className="form-label">Даты</label>
                <div className="form-row split" style={{ margin: 0 }}>
                  <div>
                    <label
                      style={{ fontSize: 12, color: "var(--text-3)", display: "block", marginBottom: 4 }}
                    >
                      Дата старта эфиров
                    </label>
                    <input
                      className="form-input"
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <label
                      style={{ fontSize: 12, color: "var(--text-3)", display: "block", marginBottom: 4 }}
                    >
                      Всего дней на доске
                    </label>
                    <div
                      className="form-input"
                      style={{ background: "var(--surface-2)", fontFamily: "JetBrains Mono, monospace" }}
                    >
                      {totalDays} {pluralDays(totalDays)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="sliders-grid">
                  <div>
                    <label className="slider-label">
                      Прогрев <b>{warmup}</b> дн.
                    </label>
                    <input
                      type="range"
                      className="range"
                      min={0}
                      max={14}
                      value={warmup}
                      onChange={(e) => setWarmup(+e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="slider-label">
                      Эфиры <b>{broadcast}</b> дн.
                    </label>
                    <input
                      type="range"
                      className="range"
                      min={0}
                      max={7}
                      value={broadcast}
                      onChange={(e) => setBroadcast(+e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="slider-label">
                      Дожим <b>{followup}</b> дн.
                    </label>
                    <input
                      type="range"
                      className="range"
                      min={0}
                      max={14}
                      value={followup}
                      onChange={(e) => setFollowup(+e.target.value)}
                    />
                  </div>
                </div>
                {seedCards && selectedType && selectedType.daysByStage && (
                  <LaunchStructureMismatchHint
                    tpl={selectedType.daysByStage}
                    real={{ warmup, sales: broadcast, close: followup }}
                  />
                )}
              </div>

              <div className="form-row">
                <label className="form-label">Превью таймлайна</label>
                <div className="timeline-preview">
                  {segments.map((s, i) => (
                    <div key={i} className="tl-segment" style={{ flex: s.days, background: s.color }}>
                      <b>{s.label}</b>
                      <span>
                        {s.days} {pluralDays(s.days)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-row">
                <label className="form-label">Набор типов карточек</label>
                <div className="typeset-list">
                  {TYPE_SETS.map((opt) => (
                    <label
                      key={opt.key}
                      className={"typeset-opt" + (typeSet === opt.key ? " active" : "")}
                    >
                      <input
                        type="radio"
                        name="typeset"
                        checked={typeSet === opt.key}
                        onChange={() => setTypeSet(opt.key)}
                      />
                      <div className="typeset-opt-main">
                        <b>{opt.label}</b>
                        <div className="typeset-chips">
                          {opt.types.length === 0 ? (
                            <span style={{ color: "var(--text-3)", fontSize: 12 }}>
                              Добавите сами
                            </span>
                          ) : (
                            opt.types.map((t) => (
                              <span
                                key={t.id}
                                className="typeset-chip"
                                style={{ background: t.bg, color: t.text }}
                              >
                                <span className="type-dot" style={{ background: t.color }} />
                                {t.name}
                              </span>
                            ))
                          )}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
                <div className="preset-hint" style={{ marginTop: 6 }}>
                  После создания список типов можно менять в «Управлять типами».
                </div>
              </div>

              {typeSet !== "empty" && (
                <div className="form-row">
                  <label className="seed-toggle">
                    <input
                      type="checkbox"
                      checked={seedCards}
                      onChange={(e) => setSeedCards(e.target.checked)}
                    />
                    <div>
                      <b>Заполнить доску шаблонными карточками</b>
                      <span>
                        Разложим preset-шаблоны по первому дню каждого этапа — письма прогрева в
                        первый день прогрева, напоминания об эфире в первый день эфиров и так далее.
                        Потом отредактируете.
                      </span>
                    </div>
                  </label>
                </div>
              )}
            </>
          ) : (
            <div className="form-row">
              <div className="clone-info">
                <IcoCopy size={14} stroke="var(--accent-text)" />
                <div>
                  <b>Копирование из существующего запуска</b>
                  <span>
                    Скопируем структуру дней, типы карточек и все карточки как черновики. Даты сдвинутся
                    от выбранной даты старта.
                  </span>
                </div>
              </div>
              <div style={{ marginTop: 12 }}>
                <label
                  style={{ fontSize: 12, color: "var(--text-3)", display: "block", marginBottom: 4 }}
                >
                  Новая дата старта эфиров
                </label>
                <input
                  className="form-input"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
            </div>
          )}
        </div>

        <div className="modal-foot">
          <div className="foot-hint">
            {cloneFrom === "" && totalDays > 30 && (
              <span style={{ color: "#DC2626" }}>⚠ Максимум 30 дней на доске</span>
            )}
            {cloneFrom === "" && totalDays === 0 && (
              <span style={{ color: "#DC2626" }}>⚠ Нужен хотя бы один день</span>
            )}
            {error && <span style={{ color: "#DC2626" }}>⚠ {error.message}</span>}
          </div>
          <div className="spacer" />
          <button type="button" className="btn-ghost" onClick={onClose} disabled={isPending}>
            Отмена
          </button>
          <button type="button" className="btn-primary" onClick={submit} disabled={!canSubmit}>
            <IcoCheck size={14} />
            {isPending ? "Создаём…" : "Создать запуск"}
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}

// Подсказка под слайдерами, когда в текущих значениях структура запуска
// отличается от структуры шаблона. Кратко объясняет, какие карточки не
// попадут / куда склеятся / какие дни останутся пустыми.
function LaunchStructureMismatchHint({
  tpl,
  real,
}: {
  tpl: { warmup: number; sales: number; close: number };
  real: { warmup: number; sales: number; close: number };
}) {
  const tplTotal = tpl.warmup + tpl.sales + tpl.close;
  if (tplTotal === 0) return null;
  const notes: string[] = [];
  // warmup — align end
  if (real.warmup < tpl.warmup) {
    notes.push(
      `прогрев: отсечём ${tpl.warmup - real.warmup} самых ранних шаблонных ${dayWord(tpl.warmup - real.warmup)}`,
    );
  } else if (real.warmup > tpl.warmup) {
    notes.push(
      `прогрев: первые ${real.warmup - tpl.warmup} дн. останутся пустыми`,
    );
  }
  // sales — align start
  if (real.sales < tpl.sales) {
    notes.push(
      `эфиры: последние ${tpl.sales - real.sales} шаблонных ${dayWord(tpl.sales - real.sales)} не попадут`,
    );
  } else if (real.sales > tpl.sales) {
    notes.push(
      `эфиры: последние ${real.sales - tpl.sales} дн. останутся пустыми`,
    );
  }
  // close — first/last anchored
  if (real.close < tpl.close) {
    notes.push(
      `дожим: первый и последний день сохранятся, середина ${tpl.close - real.close + 1}→${real.close - 1} сожмётся`,
    );
  } else if (real.close > tpl.close) {
    notes.push(
      `дожим: шаблонные дни растянутся с сохранением краёв, в середине могут быть пустые дни`,
    );
  }

  const unchanged = notes.length === 0;
  return (
    <div
      className="preset-hint"
      style={{
        marginTop: 8,
        background: unchanged ? "var(--surface-2)" : "#FEF3C7",
        color: unchanged ? "var(--text-2)" : "#92540C",
      }}
    >
      <span>
        <b>Шаблон:</b> {tpl.warmup}/{tpl.sales}/{tpl.close}.{" "}
        {unchanged ? "Ваша структура совпадает с шаблоном — карточки лягут 1-в-1." : notes.join("; ") + "."}
      </span>
    </div>
  );
}

function dayWord(n: number): string {
  if (n === 1) return "день";
  if (n >= 2 && n <= 4) return "дня";
  return "дней";
}
